import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { useUserTrialDetails } from '@revelio/auth';

import type { AllSelectionListsWithSkillTaxonomyKey } from '../../../../shared/selection-lists/types';
import { useTabContentConfig } from '../../components/PageConfigProvider/PageConfigProvider';
import { RevelioPageId } from '../../components/PageConfigProvider/types';
import { isScreenerV2Page } from '../../utils/routing-helpers';
import { useFilterContext } from '../contexts/FilterProvider';
import { getFilterDefinition } from '../engine/filterRegistry';
import {
  type Filter,
  FilterId,
  FilterType,
  type RangeFilter,
  type TreeFilter,
  type TreeSelection,
} from '../types/filters';
import { createSegmentedFilter } from '../utils/common';
import { getConsistencyKey } from '../utils/consistencyKey';

/**
 * Applies default filters for trial users on screener v2 pages.
 * Mirrors the old screener default logic but adapted for CustomDash filter architecture.
 * Only applies when there are no URL filters and no existing user selections.
 */
export const useTrialScreenerV2Defaults = () => {
  const { revelioPageId } = useTabContentConfig();
  const location = useLocation();

  const { dispatch, getFilterData, activeFilterMap, hadInitialUrlFilters } =
    useFilterContext();

  const { isTrialUser } = useUserTrialDetails<boolean>({
    initialRenderValue: false,
  });

  const [defaultsApplied, setDefaultsApplied] = useState(false);

  // biome-ignore lint/correctness/useExhaustiveDependencies: migrated from develop
  useEffect(() => {
    if (!isScreenerV2Page(revelioPageId)) {
      return;
    }

    if (defaultsApplied) {
      return;
    }

    const shouldApplyDefaults = isTrialUser && !hadInitialUrlFilters;

    if (!shouldApplyDefaults) {
      return;
    }

    // Check that filter data is ready
    try {
      const roleData = getFilterData(FilterId.ROLE);
      const industryData = getFilterData(FilterId.INDUSTRY);
      if (!Array.isArray(roleData) || roleData.length === 0) {
        return;
      }
      if (!Array.isArray(industryData) || industryData.length === 0) {
        return;
      }
    } catch {
      return;
    }

    const defaultFilterMap = buildDefaultFiltersForScreenerV2(
      revelioPageId,
      getFilterData
    );

    if (!defaultFilterMap) {
      return;
    }

    setDefaultsApplied(true);

    dispatch({
      type: 'REPLACE_FILTERS',
      payload: {
        filterMap: defaultFilterMap,
        consistencyKey: getConsistencyKey({
          revelioPageId,
          primaryFilterId: undefined,
        }),
      },
    });
  }, [
    revelioPageId,
    location.search,
    isTrialUser,
    activeFilterMap,
    hadInitialUrlFilters,
    dispatch,
    getFilterData,
    defaultsApplied,
  ]);
};

/**
 * Builds default filter map for screener v2 pages based on the page type.
 */
const buildDefaultFiltersForScreenerV2 = (
  revelioPageId: RevelioPageId | undefined,
  getFilterData: (filterId: FilterId) => unknown
): Map<FilterId, Filter> | null => {
  if (revelioPageId === RevelioPageId.COMPANY_SCREENER) {
    return buildCompanyScreenerDefaults(getFilterData);
  }
  if (revelioPageId === RevelioPageId.GEOGRAPHY_SCREENER) {
    return buildGeographyScreenerDefaults(getFilterData);
  }
  if (revelioPageId === RevelioPageId.ROLE_SCREENER) {
    return buildRoleScreenerDefaults(getFilterData);
  }
  return null;
};

/**
 * Finds a tree node by numeric ID at a specific depth.
 */
const findTreeNodeByNumericId = (
  nodes: Array<{ id: string; name: string; children?: unknown[] }>,
  targetNumericId: string,
  expectedDepth: number
): { id: string; name: string } | null => {
  for (const node of nodes) {
    const nodeNumericId = node.id.split('_').pop();
    if (nodeNumericId === targetNumericId) {
      const nodeDepth = node.id.split('_').length - 1;
      if (nodeDepth === expectedDepth) {
        return { id: node.id, name: node.name };
      }
    }
    if (node.children) {
      const found = findTreeNodeByNumericId(
        node.children as Array<{
          id: string;
          name: string;
          children?: unknown[];
        }>,
        targetNumericId,
        expectedDepth
      );
      if (found) return found;
    }
  }
  return null;
};

/**
 * Resolves a tree selection from filter data by numeric ID.
 */
const resolveTreeSelection = (
  filterId: FilterId,
  numericId: string,
  levelId: AllSelectionListsWithSkillTaxonomyKey,
  getFilterData: (filterId: FilterId) => unknown
): TreeSelection | null => {
  const filterDefinition = getFilterDefinition(filterId);
  if (!filterDefinition || filterDefinition.type !== FilterType.TREE) {
    return null;
  }

  const selectionListIds = (filterDefinition as TreeFilter).selectionListIds;
  // biome-ignore lint/complexity/useIndexOf: migrated from develop
  const expectedDepth = selectionListIds.findIndex((id) => id === levelId);
  if (expectedDepth < 0) return null;

  const treeData = getFilterData(filterId) as Array<{
    id: string;
    name: string;
    children?: unknown[];
  }> | null;
  if (!treeData) return null;

  const foundNode = findTreeNodeByNumericId(treeData, numericId, expectedDepth);

  if (!foundNode) return null;

  return { id: foundNode.id, name: foundNode.name, levelId };
};

/**
 * Company screener defaults:
 * - Industry: Professional Services (rics_k10 = 8)
 * - Headcount >= 50 (no max)
 * - Growth Rate >= 50 with Sales role segmentation
 * - Funding Stage: Series A, Series B
 * - Founder Previous Company: Alphabet
 */
const buildCompanyScreenerDefaults = (
  getFilterData: (filterId: FilterId) => unknown
): Map<FilterId, Filter> | null => {
  const filterMap = new Map<FilterId, Filter>();

  // Industry: Professional Services
  const industrySelection = resolveTreeSelection(
    FilterId.INDUSTRY,
    '8',
    'rics_k10',
    getFilterData
  );
  if (!industrySelection) return null;

  const industryDef = getFilterDefinition(FilterId.INDUSTRY);
  if (!industryDef) return null;

  filterMap.set(FilterId.INDUSTRY, {
    ...industryDef,
    selections: [industrySelection],
    isUserSubmitted: true,
    isActive: true,
  });

  // Contingent Workers: false (default, not visible as chip)
  const contingentWorkersDef = getFilterDefinition(FilterId.CONTINGENT_WORKERS);
  if (contingentWorkersDef) {
    filterMap.set(FilterId.CONTINGENT_WORKERS, {
      ...contingentWorkersDef,
      selections: [{ id: 'false', name: 'Excluded' }],
      isUserSubmitted: false,
      isActive: true,
      isVisible: false,
    });
  }

  // Headcount >= 50
  const headcountDef = getFilterDefinition(FilterId.HEADCOUNT);
  if (!headcountDef) return null;

  const headcountRange: RangeFilter = {
    ...headcountDef,
    min: 50,
    max: undefined,
    isUserSubmitted: true,
    isActive: true,
  };

  const headcountSegmented = createSegmentedFilter({
    wrappedFilter: headcountRange,
    baseProps: { ...headcountRange, id: FilterId.HEADCOUNT },
    segmentations: {},
  });

  const multiHeadcountDef = getFilterDefinition(FilterId.MULTI_HEADCOUNT);
  if (!multiHeadcountDef) return null;

  filterMap.set(FilterId.MULTI_HEADCOUNT, {
    ...multiHeadcountDef,
    instances: [headcountSegmented],
    isUserSubmitted: true,
    isActive: true,
  } as Filter);

  // Growth Rate >= 50 with Sales role segmentation
  // Use simple ID format since role tree may not be fully loaded
  const salesSelection: TreeSelection = {
    id: '1',
    name: 'Sales',
    levelId: 'role_k7',
  };

  const roleDef = getFilterDefinition(FilterId.ROLE);
  if (!roleDef) return null;

  const salesRoleFilter: TreeFilter = {
    ...roleDef,
    selections: [salesSelection],
    isUserSubmitted: true,
    isActive: true,
  };

  const growthRateDef = getFilterDefinition(FilterId.GROWTH_RATE);
  if (!growthRateDef) return null;

  const growthRateRange: RangeFilter = {
    ...growthRateDef,
    min: 50,
    max: undefined,
    isUserSubmitted: true,
    isActive: true,
  };

  const growthRateSegmented = createSegmentedFilter({
    wrappedFilter: growthRateRange,
    baseProps: { ...growthRateRange, id: FilterId.GROWTH_RATE },
    segmentations: {
      job_category: salesRoleFilter,
    },
  });

  const multiGrowthRateDef = getFilterDefinition(FilterId.MULTI_GROWTH_RATE);
  if (!multiGrowthRateDef) return null;

  filterMap.set(FilterId.MULTI_GROWTH_RATE, {
    ...multiGrowthRateDef,
    instances: [growthRateSegmented],
    isUserSubmitted: true,
    isActive: true,
  } as Filter);

  // Funding Stage: Series A, Series B
  const fundingStageDef = getFilterDefinition(FilterId.FUNDING_STAGE);
  if (!fundingStageDef) return null;

  filterMap.set(FilterId.FUNDING_STAGE, {
    ...fundingStageDef,
    selections: [
      { id: '3', name: 'Series A' },
      { id: '4', name: 'Series B' },
    ],
    isUserSubmitted: true,
    isActive: true,
  });

  // Founder Previous Company: Alphabet (company_id = 766823)
  // Use simple ID format - tree may not be fully loaded
  const alphabetSelection: TreeSelection = {
    id: '0_0_766823',
    name: 'Alphabet, Inc.',
    levelId: 'company',
  };

  const founderCompanyDef = getFilterDefinition(FilterId.FOUNDER_COMPANY);
  if (founderCompanyDef) {
    filterMap.set(FilterId.FOUNDER_COMPANY, {
      ...founderCompanyDef,
      selections: [alphabetSelection],
      isUserSubmitted: true,
      isActive: true,
    });
  }

  return filterMap;
};

/**
 * Geography screener defaults:
 * - Inflow >= 50 with Engineer role segmentation
 */
const buildGeographyScreenerDefaults = (
  _getFilterData: (filterId: FilterId) => unknown
): Map<FilterId, Filter> | null => {
  const filterMap = new Map<FilterId, Filter>();

  // Engineer role selection - use simple ID format
  const engineerSelection: TreeSelection = {
    id: '2',
    name: 'Engineer',
    levelId: 'role_k7',
  };

  const roleDef = getFilterDefinition(FilterId.ROLE);
  if (!roleDef) return null;

  const engineerRoleFilter: TreeFilter = {
    ...roleDef,
    selections: [engineerSelection],
    isUserSubmitted: true,
    isActive: true,
  };

  // Inflow >= 50
  const inflowDef = getFilterDefinition(FilterId.INFLOW);
  if (!inflowDef) return null;

  const inflowRange: RangeFilter = {
    ...inflowDef,
    min: 50,
    max: undefined,
    isUserSubmitted: true,
    isActive: true,
  };

  const inflowSegmented = createSegmentedFilter({
    wrappedFilter: inflowRange,
    baseProps: { ...inflowRange, id: FilterId.INFLOW },
    segmentations: {
      job_category: engineerRoleFilter,
    },
  });

  const multiInflowDef = getFilterDefinition(FilterId.MULTI_INFLOW);
  if (!multiInflowDef) return null;

  filterMap.set(FilterId.MULTI_INFLOW, {
    ...multiInflowDef,
    instances: [inflowSegmented],
    isUserSubmitted: true,
    isActive: true,
  } as Filter);

  // Contingent Workers: false (default, not visible as chip)
  const contingentWorkersDef = getFilterDefinition(FilterId.CONTINGENT_WORKERS);
  if (contingentWorkersDef) {
    filterMap.set(FilterId.CONTINGENT_WORKERS, {
      ...contingentWorkersDef,
      selections: [{ id: 'false', name: 'Excluded' }],
      isUserSubmitted: false,
      isActive: true,
      isVisible: false,
    });
  }

  return filterMap;
};

/**
 * Role screener defaults:
 * - Role: Engineer
 */
const buildRoleScreenerDefaults = (
  _getFilterData: (filterId: FilterId) => unknown
): Map<FilterId, Filter> | null => {
  const filterMap = new Map<FilterId, Filter>();

  // Engineer role selection - use simple ID format
  const engineerSelection: TreeSelection = {
    id: '2',
    name: 'Engineer',
    levelId: 'role_k7',
  };

  const roleDef = getFilterDefinition(FilterId.ROLE);
  if (!roleDef) return null;

  filterMap.set(FilterId.ROLE, {
    ...roleDef,
    selections: [engineerSelection],
    isUserSubmitted: true,
    isActive: true,
  });

  // Contingent Workers: false (default, not visible as chip)
  const contingentWorkersDef = getFilterDefinition(FilterId.CONTINGENT_WORKERS);
  if (contingentWorkersDef) {
    filterMap.set(FilterId.CONTINGENT_WORKERS, {
      ...contingentWorkersDef,
      selections: [{ id: 'false', name: 'Excluded' }],
      isUserSubmitted: false,
      isActive: true,
    });
  }

  return filterMap;
};
